import { useState, useCallback } from 'react';
import { useCanvas } from '../contexts/CanvasContext';
import { useDesignerStore } from '../stores';
import { usePricingStore } from '../stores/pricingStore';
import { useDesign } from './api/useDesign';
import { apiClient } from '../utils/api';

interface CartOptions {
  designId: string;
  productId: string;
  variantId?: string;
  quantity: number;
  previewUrl?: string;
}

interface AddToCartResult {
  success: boolean;
  error?: string;
}

export const useCart = () => {
  const { canvas } = useCanvas();
  const { productId, variantId, designId, setDesignId } = useDesignerStore();
  const { quantity } = usePricingStore();
  const { generatePreview } = useDesign();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if running in iframe (BigCommerce storefront)
  const isInIframe = useCallback((): boolean => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, []);

  // Send message to parent window (BigCommerce)
  const postMessageToParent = useCallback((message: object) => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  }, []);

  // Add to cart via BigCommerce postMessage API (matches legacy implementation)
  const addToCartBigCommerce = useCallback(async (options: CartOptions): Promise<AddToCartResult> => {
    // Store design info in localStorage for cart/checkout page retrieval
    const designInfo = {
      designId: options.designId,
      previewUrl: options.previewUrl,
      productId: options.productId,
      variantId: options.variantId,
      quantity: options.quantity,
      timestamp: Date.now(),
    };

    // Store by product ID
    localStorage.setItem(`mm_design_${options.productId}`, JSON.stringify(designInfo));

    // Store as latest design
    localStorage.setItem('mm_latest_design', JSON.stringify(designInfo));

    // Send cart data to parent window
    postMessageToParent({
      type: 'MM_DESIGN_ADD_TO_CART',
      data: {
        productId: String(options.productId),
        variantId: String(options.variantId || ''),
        designId: options.designId,
        designPreviewUrl: options.previewUrl,
        quantity: Number(options.quantity),  // Ensure number type
      },
    });

    // Store design info for parent window
    postMessageToParent({
      type: 'MM_STORE_DESIGN_INFO',
      data: designInfo,
    });

    // Widget script will redirect to cart
    return { success: true };
  }, [postMessageToParent]);

  // Add to cart via API (fallback for non-iframe)
  const addToCartApi = useCallback(async (options: CartOptions): Promise<AddToCartResult> => {
    try {
      const response = await fetch('/api/cart/add', {
        method: 'POST',
        credentials: 'include', // always send/receive the session cookie (cart persistence)
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: options.productId,
          variantId: options.variantId,
          quantity: options.quantity,
          designId: options.designId,
          previewUrl: options.previewUrl,
        }),
      });

      const data = await response.json();

      if (data.success) {
        return { success: true };
      } else {
        return {
          success: false,
          error: data.error || 'Failed to add to cart',
        };
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to add to cart',
      };
    }
  }, []);

  // Save design to cart endpoint (matches legacy)
  const saveDesignToCart = useCallback(async (): Promise<{ designId: string; previewUrl: string } | null> => {
    if (!canvas) return null;

    try {
      // Get canvas JSON
      const designData = canvas.toJSON([
        'id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay',
        'uploadedUrl', 'serverUrl', 'laserConverted', 'convertedFrom',
      ]);

      // Generate preview image (base64)
      const preview = await generatePreview();

      // Generate design ID if not exists (must be UUID format for database)
      const saveDesignId = designId || crypto.randomUUID();

      const response = await apiClient.post<{
        id?: string;
        designId?: string;
        previewUrl?: string;
      }>('/api/cart/design', {
        designId: saveDesignId,
        designData,
        previewImage: preview,
        productId,
        variantId,
        cartId: null,
        cartItemId: null,
        customerId: null,
        customerEmail: null,
      });

      if (response.success && response.data) {
        const savedDesignId = response.data.id || response.data.designId;
        const savedPreviewUrl = response.data.previewUrl;

        if (savedDesignId && savedPreviewUrl) {
          return {
            designId: savedDesignId,
            previewUrl: savedPreviewUrl,
          };
        }
      }

      return null;
    } catch (err) {
      return null;
    }
  }, [canvas, designId, productId, variantId, generatePreview]);

  // Main add to cart function
  const addToCart = useCallback(async (): Promise<boolean> => {
    if (!productId) {
      setError('No product selected');
      return false;
    }

    setIsAddingToCart(true);
    setError(null);

    try {
      // Save design to cart endpoint
      const saved = await saveDesignToCart();
      if (!saved) {
        throw new Error('Failed to save design');
      }

      const { designId: currentDesignId, previewUrl } = saved;

      // Update design ID in store
      setDesignId(currentDesignId);

      // Get quantity from pricing store (user's selection)
      const { quantity: selectedQuantity } = usePricingStore.getState();

      const cartOptions: CartOptions = {
        designId: currentDesignId,
        productId,
        variantId: variantId || undefined,
        quantity: Number(selectedQuantity) || 1,  // Ensure it's a number
        previewUrl,
      };

      // Use appropriate method based on context
      let result: AddToCartResult;
      if (isInIframe()) {
        result = await addToCartBigCommerce(cartOptions);
      } else {
        result = await addToCartApi(cartOptions);
      }

      if (result.success) {
        // Notify parent of success
        postMessageToParent({
          type: 'CART_UPDATED',
          payload: { action: 'add', quantity },
        });

        // Redirect to cart page when not in iframe (standalone mode)
        if (!isInIframe()) {
          // Small delay to ensure cart is updated before redirect
          setTimeout(() => {
            window.location.href = '/cart';
          }, 300);
        }

        return true;
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add to cart';
      setError(message);
      return false;
    } finally {
      setIsAddingToCart(false);
    }
  }, [
    canvas,
    productId,
    variantId,
    designId,
    quantity,
    setDesignId,
    saveDesignToCart,
    isInIframe,
    addToCartBigCommerce,
    addToCartApi,
    postMessageToParent,
  ]);

  // Close designer and return to store
  const closeDesigner = useCallback(() => {
    postMessageToParent({ type: 'CLOSE_DESIGNER' });

    // Fallback: go back in history
    if (!isInIframe()) {
      window.history.back();
    }
  }, [postMessageToParent, isInIframe]);

  return {
    addToCart,
    closeDesigner,
    isAddingToCart,
    error,
  };
};

export default useCart;
